'use client'

import Link from 'next/link'
import { ImageDisplay } from './ImageDisplay'
import {
  ArrowTopRightOnSquareIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline'
import { parseDiscoverieScopedQuery } from '@/lib/discoverie/querySyntax'
import { tracking } from '@/tracking'
import { buildDiscoverieAdminViewerHref } from '@/lib/discoverie/adminViewer'

export const SearchResult = ({
  objectId,
  downloadFileId,
  downloadFileName,
  title,
  author,
  hasPurchased,
  productUrl,
  productThumbnail,
  pages,
  searchQuery,
  resultPosition,
  resultsPage,
  isAdmin,
}: SearchResultProps) => {
  const ctaLabel = hasPurchased ? 'View book' : 'View book to buy'
  const adminViewerHref = downloadFileId
    ? buildDiscoverieAdminViewerHref(downloadFileId, pages?.[0]?.pageNumber)
    : null

  const trackProductClick = () => {
    if (!productUrl) return
    const parsed = searchQuery
      ? parseDiscoverieScopedQuery(searchQuery)
      : { baseQuery: '', titleFilter: undefined, authorFilter: undefined }

    tracking.track('Clicked Discoverie result', {
      'Product ID': objectId,
      'Product name': title,
      Author: author,
      'Has purchased': !!hasPurchased,
      'Search query': searchQuery,
      'Search term': parsed.baseQuery || undefined,
      'Title filter': parsed.titleFilter,
      'Author filter': parsed.authorFilter,
      'Result position': resultPosition,
      'Results page': resultsPage,
      CTA: ctaLabel,
    })
  }

  return (
    <div className="p-4 hover:bg-gray-50 transition-colors duration-200">
      <style jsx global>{`
                mark {
                    background-color: #fef3c7
                    color: #92400e
                    padding: 0.1em 0.2em
                    border-radius: 0.2em
                }
            `}</style>
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Book Cover */}
        <div className="flex-shrink-0">
          <div className="w-40 h-52 bg-gray-100 rounded-lg overflow-hidden shadow-md">
            {productThumbnail ? (
              <img
                src={productThumbnail}
                alt={title || 'Book cover'}
                className="w-full h-full object-cover"
              />
            ) : pages?.[0]?.image ? (
              <img
                src={pages[0].image}
                alt={title || 'Book cover'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400">
                <DocumentIcon className="w-12 h-12" />
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-1">
                {title}
              </h3>
              {author ? (
                <p className="mb-2 text-sm font-medium text-gray-600">
                  {author}
                </p>
              ) : null}
              {productUrl && (
                <Link
                  href={productUrl}
                  onClick={trackProductClick}
                  className={
                    hasPurchased
                      ? 'inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800'
                      : 'inline-flex items-center rounded-md bg-vi-red px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-800'
                  }
                >
                  {ctaLabel}
                  <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" />
                </Link>
              )}
              {isAdmin && adminViewerHref ? (
                <Link
                  href={adminViewerHref}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-2 inline-flex items-center rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-black"
                  aria-label={`View ${downloadFileName || title || 'book'} as an administrator`}
                >
                  View PDF
                  <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" />
                </Link>
              ) : null}
              {hasPurchased ? (
                <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-green-700">
                  In your library
                </p>
              ) : null}
            </div>
          </div>

          {/* Pages */}
          <div className="mt-4 space-y-3">
            {pages?.map(page => (
              <div
                key={page.pageNumber}
                className="flex items-start gap-3 w-full"
              >
                <div className="flex-shrink-0">
                  <ImageDisplay
                    imagePath={page.image}
                    pageNumber={page.pageNumber}
                  />
                </div>
                <div className="flex-grow min-w-0 w-full">
                  <p className="text-sm text-gray-600">
                    Page {page.pageNumber}
                  </p>
                  <p
                    className=" text-gray-900 mt-1 break-words w-full"
                    dangerouslySetInnerHTML={{ __html: page.textMatch }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

interface SearchResultProps {
  objectId?: string
  downloadFileId?: number
  downloadFileName?: string
  title?: string
  author?: string
  hasPurchased?: boolean
  productUrl?: string
  thumbnail?: string
  image_filename?: string
  productThumbnail?: string
  pages?: {
    pageNumber: number
    image: string
    textMatch: string
  }[]
  /** Search context passed in so the click event can be attributed to its origin search. */
  searchQuery?: string
  /** 1-based position of this result within the visible results page. */
  resultPosition?: number
  /** Current pagination page (1-based). */
  resultsPage?: number
  isAdmin: boolean
}
